import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BarChart3, Edit, Trash2, ExternalLink, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

const QRCodeCard = ({ qrCode, onDelete }) => {
  const { t } = useTranslation();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const qrUrl = `${backendUrl}/${qrCode.uniqueSlug}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(qrUrl);
    toast.success(t('link_copied'));
  };

  return (
    <div className="card p-6 space-y-4" data-testid={`qr-card-${qrCode.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-lg text-primary mb-1" data-testid="qr-card-name">{qrCode.name}</h3>
          <p className="text-xs text-muted-foreground">{qrCode.type === 'url' ? t('url_redirect') : t('content_display')}</p>
        </div>
        <div className={`px-2 py-1 rounded text-xs font-medium ${qrCode.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`} data-testid="qr-card-status">
          {qrCode.isActive ? 'Active' : 'Inactive'}
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg border flex items-center justify-center" data-testid="qr-card-preview">
        <QRCodeSVG
          value={qrUrl}
          size={120}
          fgColor={qrCode.customization?.fgColor || '#000000'}
          bgColor={qrCode.customization?.bgColor || '#FFFFFF'}
          level="H"
          includeMargin={true}
        />
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-1" data-testid="qr-card-scans">
          <BarChart3 className="h-4 w-4" />
          <span>{qrCode.scanCount || 0} {t('scans')}</span>
        </div>
        <button
          onClick={handleCopyLink}
          className="flex items-center gap-1 hover:text-accent transition-colors"
          data-testid="qr-card-copy-link-btn"
        >
          <Copy className="h-4 w-4" />
          <span className="text-xs">{t('copy_link')}</span>
        </button>
      </div>

      <div className="pt-4 border-t flex gap-2">
        <Link to={`/analytics/${qrCode.id}`} className="flex-1">
          <Button variant="outline" size="sm" className="w-full" data-testid="qr-card-analytics-btn">
            <BarChart3 className="h-4 w-4 mr-1" />
            {t('analytics')}
          </Button>
        </Link>
        <Link to={`/edit/${qrCode.id}`}>
          <Button variant="outline" size="sm" data-testid="qr-card-edit-btn">
            <Edit className="h-4 w-4" />
          </Button>
        </Link>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(qrCode.id)}
          className="text-destructive hover:text-destructive"
          data-testid="qr-card-delete-btn"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default QRCodeCard;
